import React from "react";
import {
  Box,
  Typography,
  CardContent,
  LinearProgress,
  Divider,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { NotificationStatsDto } from "../../../shared/dtos/notification/NotificationStatsDto";

interface StatusItem {
  label: string;
  value: number;
  color: "success" | "warning" | "error";
}

interface NotificationOverviewWidgetProps {
  data: NotificationStatsDto;
}

const NotificationOverviewWidget: React.FC<NotificationOverviewWidgetProps> = ({
  data,
}) => {
  const meta = getEntityMeta("NOTIFICATION");
  const Icon = meta.icon;

  const statuses: StatusItem[] = [
    { label: "Sent Successfully", value: data.sentToday, color: "success" },
    { label: "Pending/Queued", value: data.pendingToday, color: "warning" },
    { label: "Failed", value: data.failedToday, color: "error" },
  ];

  const safeTotal = data.totalToday || 1;

  return (
    <StyledCard sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Today's Volume: {data.totalToday}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 3 }}>
          {statuses.map((s, index) => (
            <Box key={index}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">{s.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {s.value}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(s.value / safeTotal) * 100}
                color={s.color}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Scheduled for Future
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "info.main" }}
          >
            {data.scheduledForFuture}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default NotificationOverviewWidget;
